<template>
  <div class="qr">
    <!-- ── Respondendo (uma categoria por vez) ── -->
    <template v-if="!result">
      <ol v-if="categories.length > 1" class="qr-steps">
        <li
          v-for="(cat, ci) in categories"
          :key="cat.id"
          class="qr-step"
          :class="{ done: ci < currentIndex, current: ci === currentIndex }"
        >
          <span class="qr-step-num">
            <svg v-if="ci < currentIndex" width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3"><polyline points="20 6 9 17 4 12"/></svg>
            <template v-else>{{ ci + 1 }}</template>
          </span>
          <span class="qr-step-name">{{ cat.name }}</span>
        </li>
      </ol>

      <div v-if="currentCategory" class="qr-progress">
        <div class="qr-progress-text">
          <template v-if="categories.length > 1">Categoria {{ currentIndex + 1 }} de {{ categories.length }} · </template>
          <strong>{{ answeredCount }}</strong> de {{ currentCategory.questions.length }} respondidas
        </div>
        <div class="qr-progress-bar"><div class="qr-progress-fill" :style="{ width: progressPct + '%' }"></div></div>
      </div>

      <section v-for="(cat, ci) in categories" :key="cat.id" class="qr-cat" :class="{ 'qr-cat--done': ci < currentIndex, 'qr-cat--locked': ci > currentIndex }">
        <header class="qr-cat-head">
          <span class="qr-cat-badge">{{ ci + 1 }}</span>
          <div class="qr-cat-title">
            <span class="qr-cat-name">{{ cat.name }}</span>
            <span class="qr-cat-meta">{{ cat.questions.length }} pergunta{{ cat.questions.length === 1 ? '' : 's' }}</span>
          </div>
          <span v-if="ci < currentIndex" class="qr-cat-state qr-cat-state--done">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3"><polyline points="20 6 9 17 4 12"/></svg>
            Enviada
          </span>
          <span v-else-if="ci > currentIndex" class="qr-cat-state">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2"><rect x="4" y="11" width="16" height="10" rx="2"/><path d="M8 11V7a4 4 0 0 1 8 0v4"/></svg>
            Abre após a anterior
          </span>
        </header>

        <div v-if="ci === currentIndex" class="qr-cat-body">
          <article v-for="(q, qi) in cat.questions" :key="q.id" class="qr-question" :class="{ 'qr-question--done': answers[q.id] !== undefined }">
            <div class="qr-q-head">
              <span class="qr-q-num">{{ qi + 1 }}</span>
              <p class="qr-q-text">{{ q.text }}</p>
            </div>
            <div class="qr-options" role="radiogroup">
              <label
                v-for="(opt, oi) in q.options"
                :key="oi"
                class="qr-option"
                :class="{ selected: answers[q.id] === oi }"
              >
                <input class="qr-radio" type="radio" :name="'q-' + q.id" :checked="answers[q.id] === oi" @change="select(q.id, oi)" />
                <span class="qr-letter">{{ letter(oi) }}</span>
                <span class="qr-opt-text">{{ opt }}</span>
              </label>
            </div>
          </article>

          <div class="qr-footer">
            <p v-if="confirmUnanswered" class="qr-warn">
              Você deixou {{ cat.questions.length - answeredCount }} pergunta(s) sem resposta nesta categoria — elas contam como erradas. Clique novamente para confirmar.
            </p>
            <button class="qr-submit" type="button" :disabled="submitting" @click="onSubmit">
              {{ submitting ? 'Enviando...' : isLast ? 'Enviar e finalizar' : 'Enviar e continuar' }}
            </button>
          </div>
        </div>
      </section>
    </template>

    <!-- ── Resumo final ── -->
    <template v-else>
      <div class="qr-score">
        <div class="qr-score-ring" :class="tone(result.scorePercent)" :style="{ '--pct': result.scorePercent }">
          <span>{{ formatPct(result.scorePercent) }}</span>
        </div>
        <div class="qr-score-info">
          <h3 class="qr-score-title">{{ scoreTitle }}</h3>
          <p class="qr-score-sub">
            Você acertou <strong>{{ result.correctCount }}</strong> de <strong>{{ result.totalCount }}</strong> perguntas.
          </p>
          <p class="qr-score-meta">
            <template v-if="participantName">{{ participantName }} · </template>Enviado em {{ formatDateTime(result.submittedAt) }}
          </p>
        </div>
        <button v-if="allowRetry" class="qr-retry" type="button" @click="$emit('restart')">Fazer novamente</button>
      </div>

      <div v-if="result.categoryScores && result.categoryScores.length > 1" class="qr-catscores">
        <h4 class="qr-section-title">Resultado por categoria</h4>
        <div v-for="cs in result.categoryScores" :key="cs.categoryId" class="qr-catscore">
          <div class="qr-catscore-head">
            <span class="qr-catscore-name">{{ cs.name }}</span>
            <span class="qr-catscore-val"><strong>{{ formatPct(cs.scorePercent) }}</strong> · {{ cs.correctCount }}/{{ cs.totalCount }}</span>
          </div>
          <div class="qr-catscore-bar"><div class="qr-catscore-fill" :class="tone(cs.scorePercent)" :style="{ width: cs.scorePercent + '%' }"></div></div>
        </div>
      </div>

      <section v-for="group in resultGroups" :key="group.key" class="qr-cat qr-cat--result">
        <button class="qr-cat-head qr-cat-head--toggle" type="button" @click="toggleGroup(group.key)">
          <svg class="qr-chevron" :class="{ open: openGroups[group.key] }" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.6"><polyline points="9 18 15 12 9 6"/></svg>
          <div class="qr-cat-title">
            <span class="qr-cat-name">{{ group.name || 'Respostas' }}</span>
            <span class="qr-cat-meta">{{ group.correct }} certa{{ group.correct === 1 ? '' : 's' }} · {{ group.items.length - group.correct }} errada{{ group.items.length - group.correct === 1 ? '' : 's' }}</span>
          </div>
        </button>
        <div v-if="openGroups[group.key]" class="qr-cat-body">
          <article v-for="(r, ri) in group.items" :key="r.questionId" class="qr-question qr-question--result" :class="r.isCorrect ? 'is-correct' : 'is-wrong'">
            <div class="qr-q-head">
              <span class="qr-q-num">{{ ri + 1 }}</span>
              <p class="qr-q-text">{{ r.text }}</p>
              <span class="qr-badge" :class="r.isCorrect ? 'qr-badge--ok' : 'qr-badge--err'">{{ r.isCorrect ? 'Acertou' : 'Errou' }}</span>
            </div>
            <div class="qr-options">
              <div
                v-for="(opt, oi) in r.options"
                :key="oi"
                class="qr-option qr-option--static"
                :class="{
                  'mark-ok': r.selectedIndex === oi && r.isCorrect,
                  'mark-err': r.selectedIndex === oi && !r.isCorrect,
                  'mark-answer': r.correctIndex === oi && r.selectedIndex !== oi
                }"
              >
                <span class="qr-letter">{{ letter(oi) }}</span>
                <span class="qr-opt-text">{{ opt }}</span>
                <span v-if="r.selectedIndex === oi" class="qr-opt-tag">Sua resposta</span>
                <span v-else-if="r.correctIndex === oi" class="qr-opt-tag">Resposta correta</span>
              </div>
              <p v-if="r.selectedIndex === null" class="qr-blank">Sem resposta</p>
            </div>
          </article>
        </div>
      </section>
    </template>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, PropType, ref, watch } from 'vue'

export interface QuizRunnerQuestion { id: string; text: string; options: string[] }
export interface QuizRunnerCategory { id: string; name: string; questions: QuizRunnerQuestion[] }
export interface QuizRunnerResultItem {
  questionId: string; text: string; options: string[]
  selectedIndex: number | null; isCorrect: boolean; correctIndex?: number
  categoryId?: string; categoryName?: string
}
export interface QuizCategoryScore { categoryId: string; name: string; correctCount: number; totalCount: number; scorePercent: number }
export interface QuizRunnerResult {
  correctCount: number; totalCount: number; scorePercent: number; submittedAt: string
  categoryScores?: QuizCategoryScore[]
  results: QuizRunnerResultItem[]
}

export default defineComponent({
  name: 'QuizRunner',
  props: {
    categories: { type: Array as PropType<QuizRunnerCategory[]>, required: true },
    // índice da categoria em resposta (as anteriores já foram enviadas)
    currentIndex: { type: Number, default: 0 },
    result: { type: Object as PropType<QuizRunnerResult | null>, default: null },
    submitting: { type: Boolean, default: false },
    participantName: { type: String, default: '' },
    allowRetry: { type: Boolean, default: true }
  },
  emits: ['submit-category', 'restart'],
  setup(props, { emit }) {
    const answers = ref<Record<string, number>>({})
    const confirmUnanswered = ref(false)
    const openGroups = ref<Record<string, boolean>>({})

    watch(() => props.categories, () => { answers.value = {}; confirmUnanswered.value = false })
    watch(() => props.currentIndex, () => { confirmUnanswered.value = false })

    const currentCategory = computed(() => props.categories[props.currentIndex] || null)
    const isLast = computed(() => props.currentIndex >= props.categories.length - 1)
    const answeredCount = computed(() => (currentCategory.value?.questions || []).filter(q => answers.value[q.id] !== undefined).length)
    const progressPct = computed(() => {
      const total = currentCategory.value?.questions.length || 0
      return total ? Math.round((answeredCount.value / total) * 100) : 0
    })

    const select = (qid: string, index: number) => {
      answers.value = { ...answers.value, [qid]: index }
      confirmUnanswered.value = false
    }

    const onSubmit = () => {
      const cat = currentCategory.value
      if (!cat) return
      if (answeredCount.value < cat.questions.length && !confirmUnanswered.value) {
        confirmUnanswered.value = true
        return
      }
      const catAnswers: Record<string, number> = {}
      cat.questions.forEach(q => { if (answers.value[q.id] !== undefined) catAnswers[q.id] = answers.value[q.id] })
      emit('submit-category', { categoryId: cat.id, answers: catAnswers })
    }

    // Respostas do resumo agrupadas por categoria (na ordem em que vieram)
    const resultGroups = computed(() => {
      const groups: { key: string; name: string; items: QuizRunnerResultItem[]; correct: number }[] = []
      ;(props.result?.results || []).forEach(r => {
        const key = r.categoryId || '_'
        let g = groups.find(x => x.key === key)
        if (!g) { g = { key, name: r.categoryName || '', items: [], correct: 0 }; groups.push(g) }
        g.items.push(r)
        if (r.isCorrect) g.correct++
      })
      return groups
    })
    watch(resultGroups, (groups) => {
      // uma categoria só: já abre; várias: começam fechadas (o resumo por categoria fica em cima)
      openGroups.value = Object.fromEntries(groups.map(g => [g.key, groups.length === 1]))
    }, { immediate: true })
    const toggleGroup = (key: string) => { openGroups.value = { ...openGroups.value, [key]: !openGroups.value[key] } }

    const letter = (i: number) => String.fromCharCode(65 + i)
    const formatPct = (v: number) => `${Number(v || 0).toLocaleString('pt-BR', { maximumFractionDigits: 1 })}%`
    const formatDateTime = (iso: string) => {
      const d = new Date(iso)
      return Number.isNaN(d.getTime()) ? '' : d.toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' })
    }
    const tone = (p: number) => (p >= 70 ? 'tone-ok' : p >= 50 ? 'tone-mid' : 'tone-low')
    const scoreTitle = computed(() => {
      const p = props.result?.scorePercent || 0
      return p >= 70 ? 'Muito bem!' : p >= 50 ? 'Quase lá' : 'Continue praticando'
    })

    return {
      answers, confirmUnanswered, openGroups, currentCategory, isLast, answeredCount, progressPct,
      select, onSubmit, resultGroups, toggleGroup, letter, formatPct, formatDateTime, tone, scoreTitle
    }
  }
})
</script>

<style scoped lang="scss">
.qr { display: flex; flex-direction: column; gap: 12px; }

.qr-steps { display: flex; gap: 6px; flex-wrap: wrap; margin: 0; padding: 0; list-style: none; }
.qr-step {
  display: inline-flex; align-items: center; gap: 7px;
  padding: 5px 12px 5px 5px; border-radius: 20px;
  border: 1px solid var(--border, rgba(0,0,0,.1)); background: var(--surface, #fff);
  font-size: 12px; font-weight: 600; color: var(--ink-muted, #605e5c);
  &.current { border-color: var(--system-navbar, #0f4c81); color: var(--ink, #201f1e); }
  &.done { color: #107c10; }
}
.qr-step-num {
  width: 22px; height: 22px; border-radius: 50%; flex-shrink: 0;
  display: inline-flex; align-items: center; justify-content: center;
  background: var(--border, rgba(0,0,0,.08)); font-size: 11px; font-weight: 700;
  .current & { background: var(--system-navbar, #0f4c81); color: var(--system-navbar-text, #fff); }
  .done & { background: rgba(16,124,16,.14); color: #107c10; }
}
.qr-step-name { max-width: 180px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }

.qr-progress {
  position: sticky; top: 0; z-index: 2;
  display: flex; align-items: center; gap: 14px;
  padding: 10px 14px; border-radius: var(--radius, 6px);
  background: var(--surface, #fff); border: 1px solid var(--border, rgba(0,0,0,.1)); box-shadow: var(--shadow-sm);
}
.qr-progress-text { font-size: 12px; color: var(--ink-muted, #605e5c); white-space: nowrap; strong { color: var(--ink, #201f1e); } }
.qr-progress-bar { flex: 1; height: 4px; border-radius: 2px; background: var(--border, rgba(0,0,0,.1)); overflow: hidden; }
.qr-progress-fill { height: 100%; background: var(--system-navbar, #0f4c81); transition: width .2s; }

.qr-cat {
  border: 1px solid var(--border, rgba(0,0,0,.1)); border-radius: var(--radius-lg, 10px);
  background: var(--card, #fff); overflow: hidden;
  &--done { background: var(--surface, #fff); }
  &--locked { opacity: .65; }
}
.qr-cat-head {
  display: flex; align-items: center; gap: 10px; width: 100%;
  padding: 12px 16px; border: none; background: transparent; text-align: left; font-family: inherit;
  &--toggle { cursor: pointer; &:hover { background: rgba(0,0,0,.025); } }
}
.qr-cat-badge {
  width: 26px; height: 26px; border-radius: 6px; flex-shrink: 0;
  display: inline-flex; align-items: center; justify-content: center;
  background: var(--system-navbar, #0f4c81); color: var(--system-navbar-text, #fff); font-size: 12px; font-weight: 700;
  .qr-cat--done & { background: rgba(16,124,16,.14); color: #107c10; }
  .qr-cat--locked & { background: var(--border, rgba(0,0,0,.1)); color: var(--ink-muted, #605e5c); }
}
.qr-cat-title { flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 1px; }
.qr-cat-name { font-size: 14px; font-weight: 700; color: var(--ink, #201f1e); }
.qr-cat-meta { font-size: 12px; color: var(--ink-muted, #605e5c); }
.qr-cat-state {
  display: inline-flex; align-items: center; gap: 5px; flex-shrink: 0;
  font-size: 12px; font-weight: 600; color: var(--ink-muted, #605e5c);
  &--done { color: #107c10; }
}
.qr-cat-body { display: flex; flex-direction: column; gap: 10px; padding: 4px 14px 14px; }
.qr-chevron { flex-shrink: 0; color: var(--ink-muted, #605e5c); transition: transform .15s; &.open { transform: rotate(90deg); } }

.qr-question {
  background: var(--card, #fff); border: 1px solid var(--border, rgba(0,0,0,.1));
  border-radius: var(--radius-lg, 10px); padding: 16px 18px;
  border-left: 3px solid transparent;
  &--done { border-left-color: var(--system-navbar, #0f4c81); }
  &.is-correct { border-left-color: #107c10; }
  &.is-wrong { border-left-color: #a4262c; }
}
.qr-q-head { display: flex; align-items: flex-start; gap: 10px; margin-bottom: 12px; }
.qr-q-num {
  flex-shrink: 0; width: 24px; height: 24px; border-radius: 50%;
  display: inline-flex; align-items: center; justify-content: center;
  background: var(--accent-light, #deecf9); color: var(--system-navbar, #0f4c81); font-size: 12px; font-weight: 700;
}
.qr-q-text { flex: 1; margin: 2px 0 0; font-size: 14px; font-weight: 600; color: var(--ink, #201f1e); line-height: 1.45; white-space: pre-wrap; }

.qr-options { display: flex; flex-direction: column; gap: 6px; padding-left: 34px; }
.qr-option {
  position: relative; display: flex; align-items: center; gap: 10px;
  padding: 9px 12px; border-radius: var(--radius, 6px);
  border: 1px solid var(--border, rgba(0,0,0,.1)); background: var(--surface, #fff);
  cursor: pointer; font-size: 13px; color: var(--ink, #201f1e);
  transition: background .12s, border-color .12s;
  &:hover { background: var(--accent-light, #deecf9); }
  &.selected { border-color: var(--system-navbar, #0f4c81); background: var(--accent-light, #deecf9); }
  &.selected .qr-letter { background: var(--system-navbar, #0f4c81); color: var(--system-navbar-text, #fff); border-color: transparent; }
  &--static { cursor: default; &:hover { background: var(--surface, #fff); } }
  &.mark-ok { border-color: #107c10; background: rgba(16,124,16,.08); .qr-letter { background: #107c10; color: #fff; border-color: transparent; } }
  &.mark-err { border-color: #a4262c; background: rgba(164,38,44,.08); .qr-letter { background: #a4262c; color: #fff; border-color: transparent; } }
  &.mark-answer { border-style: dashed; border-color: #107c10; }
}
/* preso ao .qr-option (position: relative) para não escapar do container com scroll */
.qr-radio { position: absolute; inset: 0; width: 100%; height: 100%; margin: 0; opacity: 0; pointer-events: none; }
.qr-letter {
  flex-shrink: 0; width: 22px; height: 22px; border-radius: 50%;
  display: inline-flex; align-items: center; justify-content: center;
  border: 1px solid var(--border-strong, rgba(0,0,0,.2)); font-size: 11px; font-weight: 700; color: var(--ink-muted, #605e5c);
}
.qr-opt-text { flex: 1; line-height: 1.4; }
.qr-opt-tag { font-size: 11px; font-weight: 600; color: var(--ink-muted, #605e5c); white-space: nowrap; }
.qr-blank { margin: 2px 0 0; font-size: 12px; font-style: italic; color: #a4262c; }

.qr-badge { flex-shrink: 0; font-size: 11px; font-weight: 700; padding: 2px 9px; border-radius: 10px;
  &--ok { background: rgba(16,124,16,.12); color: #107c10; }
  &--err { background: rgba(164,38,44,.12); color: #a4262c; }
}

.qr-footer { display: flex; flex-direction: column; align-items: flex-end; gap: 8px; padding-top: 4px; }
.qr-warn { margin: 0; font-size: 12px; color: var(--warning, #d83b01); text-align: right; max-width: 520px; }
.qr-submit {
  padding: 9px 22px; border-radius: var(--radius, 6px); border: none; cursor: pointer;
  background: var(--system-navbar, #0f4c81); color: var(--system-navbar-text, #fff); font-size: 13px; font-weight: 600;
  &:hover:not(:disabled) { opacity: .9; }
  &:disabled { opacity: .6; cursor: not-allowed; }
}

.qr-score {
  display: flex; align-items: center; gap: 18px; flex-wrap: wrap;
  padding: 18px 20px; border-radius: var(--radius-lg, 10px);
  background: var(--card, #fff); border: 1px solid var(--border, rgba(0,0,0,.1)); box-shadow: var(--shadow-sm);
}
.qr-score-ring {
  --tone: #107c10;
  width: 76px; height: 76px; border-radius: 50%; flex-shrink: 0;
  display: flex; align-items: center; justify-content: center;
  background: conic-gradient(var(--tone) calc(var(--pct) * 1%), var(--border, rgba(0,0,0,.1)) 0);
  span {
    width: 60px; height: 60px; border-radius: 50%; background: var(--card, #fff);
    display: flex; align-items: center; justify-content: center;
    font-size: 15px; font-weight: 800; color: var(--ink, #201f1e);
  }
  &.tone-mid { --tone: #d83b01; }
  &.tone-low { --tone: #a4262c; }
}
.qr-score-info { flex: 1; min-width: 200px; }
.qr-score-title { margin: 0 0 4px; font-size: 17px; font-weight: 700; color: var(--ink, #201f1e); }
.qr-score-sub { margin: 0 0 4px; font-size: 13px; color: var(--ink, #201f1e); }
.qr-score-meta { margin: 0; font-size: 12px; color: var(--ink-muted, #605e5c); }
.qr-retry {
  padding: 8px 16px; border-radius: var(--radius, 6px); cursor: pointer;
  border: 1px solid var(--border-strong, rgba(0,0,0,.2)); background: var(--surface, #fff);
  color: var(--ink, #201f1e); font-size: 13px; font-weight: 600;
  &:hover { background: var(--accent-light, #deecf9); }
}

.qr-catscores {
  display: flex; flex-direction: column; gap: 12px; padding: 16px 18px; border-radius: var(--radius-lg, 10px);
  background: var(--card, #fff); border: 1px solid var(--border, rgba(0,0,0,.1));
}
.qr-section-title { margin: 0; font-size: 13px; font-weight: 700; color: var(--ink, #201f1e); }
.qr-catscore-head { display: flex; justify-content: space-between; gap: 10px; margin-bottom: 5px; font-size: 13px; }
.qr-catscore-name { color: var(--ink, #201f1e); font-weight: 600; }
.qr-catscore-val { color: var(--ink-muted, #605e5c); white-space: nowrap; strong { color: var(--ink, #201f1e); } }
.qr-catscore-bar { height: 8px; border-radius: 4px; background: var(--border, rgba(0,0,0,.1)); overflow: hidden; }
.qr-catscore-fill {
  height: 100%; border-radius: 4px; transition: width .3s;
  &.tone-ok { background: #107c10; } &.tone-mid { background: #d83b01; } &.tone-low { background: #a4262c; }
}

@media (max-width: 600px) {
  .qr-options { padding-left: 0; }
  .qr-question { padding: 14px; }
  .qr-cat-body { padding: 4px 8px 10px; }
}
</style>
